/* AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2024 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { useFlag } from "@unleash/proxy-client-react";
import { UnsupportedPDFViewer } from "./UnsupportedPDFViewer";
import { PDFTooLarge } from "./PDFTooLarge";
import { isSafari, prodModeLog } from "../../utils/utils";

// PDF open parameters asking the browser's built-in viewer to fit the page width:
// Chromium honours `view=FitH`, Firefox (pdf.js) honours `zoom=page-width`; each ignores the other.
// Both viewers keep that fit mode and re-fit on resize until the user zooms manually.
const PDF_FIT_TO_WIDTH_PARAMS = "#view=FitH&zoom=page-width";

interface IDocumentViewer {
  src?: string;
  srcDoc?: string;
  type?: string;
  isDark?: boolean;
  isLargestFile?: boolean; // > size > 2MB
  isFullHeight?: boolean;
  isMobile?: boolean;
  setPdfLoaded?: (loaded: boolean) => void;
  openNewTab?: () => void;
}

export const DocumentViewer: FC<IDocumentViewer> = ({
  src,
  srcDoc,
  type = "PDF",
  isDark = false,
  isLargestFile = false,
  isFullHeight = false,
  openNewTab,
  setPdfLoaded,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const enhancedRadpairHtmlStyle = useFlag("enhance-radpair-html-style");

  const [safariUnsupported, setSafariUnSupported] = useState(false);

  const supportedOnceRef = useRef(false);

  const iframeSrc = useMemo(() => {
    if (!src || type !== "PDF" || src.includes("#")) return src;
    return `${src}${PDF_FIT_TO_WIDTH_PARAMS}`;
  }, [src, type]);

  useEffect(() => {
    if ((!navigator.pdfViewerEnabled && type === "PDF") || isLargestFile) {
      setPdfLoaded?.(true);
    }
  }, [src, isLargestFile, setPdfLoaded, type]);

  useEffect(() => {
    if (!iframeRef?.current || !isSafari() || supportedOnceRef.current) return;

    const iframe = iframeRef.current;

    const check = (tries: number) => {
      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        // @ts-ignore
        const viewer = doc?.children?.length > 0;

        if (viewer) {
          prodModeLog("Safari PDF viewer found");
          setSafariUnSupported(false);
          supportedOnceRef.current = true; // stop future checks
        } else {
          prodModeLog("Safari did not detect PDF viewer");
          if (tries === 5) setSafariUnSupported(true);
        }
      } catch {
        prodModeLog("Cannot access iframe (cross-origin?)");
        setSafariUnSupported(true);
      }
    };

    iframe.onload = () => {
      setPdfLoaded?.(true);

      let tries = 0;
      const maxTries = 6;

      const tick = () => {
        if (supportedOnceRef.current) return; // stop if already supported
        tries++;
        check(tries);
        if (tries < maxTries) setTimeout(tick, 500);
      };

      tick();
    };
  }, [iframeRef, setPdfLoaded]);

  // ----------- render paths -----------
  if (safariUnsupported && type === "PDF") {
    return <UnsupportedPDFViewer isDark={isDark} />;
  }

  if (!isSafari() && !navigator?.pdfViewerEnabled && type === "PDF") {
    return <UnsupportedPDFViewer isDark={isDark} />;
  }

  if (isLargestFile) {
    return (
      <PDFTooLarge
        isDark={isDark}
        isFullHeight={isFullHeight}
        onClick={() => openNewTab?.()}
      />
    );
  }

  return (
    <div className={`w-100 h-100 ${isDark ? "bg-dark" : "bg-white"}`}>
      {src && (
        <iframe
          src={iframeSrc}
          style={{
            display: "block",
            width: "100%",
            minHeight: 100,
            height: "100%",
          }}
          id="reportId"
          name="report"
          ref={iframeRef}
          frameBorder="0"
          onLoad={() => setPdfLoaded?.(true)}
        />
      )}

      {srcDoc && (
        <div
          className={`document-viewer-html w-100 p-lr-sm p-t-xs ${
            isDark ? "viewer-negative" : "viewer-original"
          } ${enhancedRadpairHtmlStyle ? "enhanced-radpair-html" : ""}`}
          dangerouslySetInnerHTML={{ __html: srcDoc || "" }}
        />
      )}
    </div>
  );
};
